import { ArcElement, Chart as ChartJS, Tooltip } from "chart.js"
import { Doughnut } from "react-chartjs-2"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

ChartJS.register(ArcElement, Tooltip)

const COST = "#94a3b8"
const PROFIT = "#1e7a4c"
const LOSS = "#c2412d"

type Figure = { revenue: string; totalCost: string; profit: string; margin: number | null }

const millions = (rial: number) => rial / 1_000_000
const fmt = (value: number) => value.toLocaleString("fa-IR", { maximumFractionDigits: 0 })

/**
 * Revenue split into what it paid for: cost + profit. When the period made a loss,
 * cost is the whole ring instead — the part revenue covered, and the loss in red.
 */
/** `compact` stacks chart over legend (for three-up rows); `centerProfit` puts profit, not revenue, in the hole. */
export function PlanDonut({ title, description, figure, compact, centerProfit }: { title: string; description: string; figure: Figure; compact?: boolean; centerProfit?: boolean }) {
  const revenue = millions(Number(figure.revenue))
  const cost = millions(Number(figure.totalCost))
  const profit = millions(Number(figure.profit))
  const loss = profit < 0
  const empty = revenue === 0 && cost === 0

  const segments = loss
    ? [{ label: "هزینه پوشش‌داده‌شده با درآمد", value: revenue, color: COST }, { label: "زیان", value: -profit, color: LOSS }]
    : [{ label: "هزینه", value: cost, color: COST }, { label: "سود", value: profit, color: PROFIT }]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className={compact ? "grid items-center gap-5" : "grid items-center gap-6 sm:grid-cols-[minmax(0,220px)_1fr]"}>
        {empty ? (
          <p className="py-10 text-center text-sm text-muted-foreground sm:col-span-2">هنوز عددی برای این دوره ثبت نشده است.</p>
        ) : (
          <>
            <div className="relative mx-auto aspect-square w-full max-w-[220px]">
              <Doughnut
                data={{ labels: segments.map((segment) => segment.label), datasets: [{ data: segments.map((segment) => segment.value), backgroundColor: segments.map((segment) => segment.color), borderWidth: 2, borderColor: "#ffffff", hoverOffset: 4 }] }}
                options={{
                  cutout: "68%",
                  maintainAspectRatio: true,
                  animation: { duration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 400 },
                  plugins: {
                    legend: { display: false },
                    tooltip: { rtl: true, textDirection: "rtl", callbacks: { label: (context) => `${context.label}: ${fmt(Number(context.parsed))} میلیون ریال` } },
                  },
                }}
                aria-label={`${title}: درآمد ${fmt(revenue)}، هزینه ${fmt(cost)}، ${loss ? "زیان" : "سود"} ${fmt(Math.abs(profit))} میلیون ریال`}
                role="img"
              />
              <div className="pointer-events-none absolute inset-0 grid place-content-center text-center">
                <span className="text-xs text-muted-foreground">{centerProfit ? (loss ? "زیان" : "سود") : "درآمد"}</span>
                <span className={`text-2xl font-semibold tabular-nums ${centerProfit && loss ? "text-loss" : ""}`}>{fmt(centerProfit ? Math.abs(profit) : revenue)}</span>
                <span className="text-[11px] text-muted-foreground">میلیون ریال</span>
              </div>
            </div>
            <dl className="grid gap-3 text-sm">
              <LegendRow label="درآمد" value={revenue} />
              <LegendRow label="هزینه" value={cost} swatch={COST} />
              <LegendRow label={loss ? "زیان" : "سود"} value={profit} swatch={loss ? LOSS : PROFIT} strong />
              {figure.margin !== null && (
                <div className="flex items-center justify-between border-t pt-3 text-muted-foreground">
                  <dt>حاشیه سود</dt>
                  <dd className="tabular-nums" dir="ltr">{(figure.margin * 100).toLocaleString("fa-IR", { maximumFractionDigits: 1 })}٪</dd>
                </div>
              )}
            </dl>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function LegendRow({ label, value, swatch, strong }: { label: string; value: number; swatch?: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="flex items-center gap-2">
        <span aria-hidden className="size-2.5 rounded-full border" style={{ background: swatch ?? "transparent" }} />
        {label}
      </dt>
      <dd className={`tabular-nums ${strong ? "font-semibold" : ""} ${value < 0 ? "text-loss" : ""}`}>
        {fmt(value)} <span className="text-xs font-normal text-muted-foreground">میلیون ریال</span>
      </dd>
    </div>
  )
}
